import { Capacitor } from '@capacitor/core';
import {
  NativePurchases,
  PURCHASE_TYPE,
  type Transaction,
} from '@capgo/native-purchases';

import { ApiPath } from '@/common/app/api-path';
import { ENV } from '@/common/app/env';
import { WebsocketEmitters } from '@/common/app/socket';
import { socket } from '@/common/socket/socket';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';

/**
 * App Store purchases of the standalone iOS app (StoreKit 2).
 *
 * The server creates an ordinary game invoice with currency `apple` and says
 * which App Store product to buy for it; the invoice id goes in as the
 * appAccountToken. After the purchase the signed transaction is delivered to
 * the server, and only once the server has credited it is the transaction
 * finished — so a crash in between is recovered on the next launch.
 */

// Must match appleIap in the backend config: every purchase is sold as the
// cheapest price point >= its USD price; VIP has its own App Store prices.
const PRODUCT_PREFIX = 'com.starindustry.game.';
const APPLE_PRICE_TIERS = [
  0.29, 0.49, 0.59, 0.99, 1.29, 1.49, 1.99, 2.99, 3.99, 4.99, 5.99, 6.99, 7.49,
  7.99, 8.99, 9.99, 11.99, 14.99, 19.99, 24.99, 29.99, 39.99, 49.99, 74.99,
  99.99,
];
const APPLE_VIP: Record<number, { price: number; subscription?: string }> = {
  7: { price: 2.99, subscription: `${PRODUCT_PREFIX}vip.week` },
  30: { price: 9.99, subscription: `${PRODUCT_PREFIX}vip.month` },
  90: { price: 24.99, subscription: `${PRODUCT_PREFIX}vip.3months` },
  180: { price: 39.99, subscription: `${PRODUCT_PREFIX}vip.6months` },
};

const packProductId = (usd: number): string | undefined => {
  const tier = APPLE_PRICE_TIERS.find((t) => t >= usd - 1e-9);

  return tier === undefined
    ? undefined
    : `${PRODUCT_PREFIX}pack.${String(Math.round(tier * 100)).padStart(4, '0')}`;
};

/** App Store product a purchase of this USD price is sold as. */
const appleProductFor = (usd: number, vipDays?: number): string | undefined => {
  if (vipDays) {
    const vip = APPLE_VIP[vipDays];

    return vip ? (vip.subscription ?? packProductId(vip.price)) : undefined;
  }

  return packProductId(usd);
};

/** "$0.99" style fallback until StoreKit has returned the localized price. */
const appleFallbackPrice = (productId: string): string => {
  const vip = Object.values(APPLE_VIP).find(
    (v) => v.subscription === productId,
  );
  const cents = Number(productId.split('.pack.')[1]);

  return `$${(vip ? vip.price : cents / 100).toFixed(2)}`;
};

type AppleInvoice = {
  id: string;
  productId: string;
  appAccountToken: string;
  amount: number;
};

type PurchaseResult = 'paid' | 'cancelled' | 'failed';

const isSubscription = (productId: string): boolean =>
  productId.includes('.vip.');

const isAppleIapAvailable = (): boolean => Capacitor.getPlatform() === 'ios';

let configured = false;

const configure = async (): Promise<void> => {
  if (configured) return;
  configured = true;
  // We finish transactions ourselves, after the server has credited them
  await NativePurchases.configure({ autoFinishTransactions: false });
};

const authToken = (): string | null => localStorage.getItem('auth_token');

/** Hands a transaction to the server; resolves true once it is credited. */
const deliver = async (transaction: Transaction): Promise<boolean> => {
  const signedTransaction = transaction.jwsRepresentation;

  if (!signedTransaction) return false;

  const token = authToken();
  const response = await fetch(ENV.API_BACKEND_URL + ApiPath.APPLE_VERIFY, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-SESSION': token } : {}),
    },
    body: JSON.stringify({ signedTransaction }),
  });

  if (!response.ok) return false;

  await NativePurchases.finishTransaction({
    transactionId: transaction.transactionId,
  });

  return true;
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/** The real StoreKit reason, instead of the flows' generic error toast. */
const reportStoreKitProblem = (message: string): void => {
  console.warn('[apple-iap]', message);
  showToast(`App Store: ${message}`, undefined, { toastId: 'apple-iap' });
};

const isCancellation = (error: unknown): boolean =>
  /cancel/i.test(error instanceof Error ? error.message : String(error));

/**
 * While the App Store sheet is up iOS suspends the web view, so the game's
 * socket is usually dead by the time the purchase completes — and the
 * server's "credited" events (which drive every purchase window) would be
 * lost. So the socket is reconnected and authorized first, and only then is
 * the transaction handed to the server.
 */
const reconnectSocket = (): Promise<void> =>
  new Promise((resolve) => {
    if (!authToken()) {
      resolve();

      return;
    }

    const done = (): void => {
      clearTimeout(timer);
      socket.off(WebsocketEmitters.AUTH_OK, done);
      resolve();
    };
    const timer = setTimeout(done, 8000);

    socket.on(WebsocketEmitters.AUTH_OK, done);
    socket.disconnect();
    socket.connect();
  });

/**
 * After the server confirmed the purchase: a purchase window still waiting
 * ("in progress") switches to success — the shared socket handlers do this
 * for some purchases only — and the balances are refreshed in case the
 * socket events were lost after all. iOS app only; the shared purchase
 * mechanism is untouched.
 */
const afterPurchaseCredited = (credited: boolean): void => {
  setTimeout(() => {
    void (async (): Promise<void> => {
      const [{ default: store }, { userApi }, { changePaymentStatus }] =
        await Promise.all([
          import('@/redux/store'),
          import('@/api/queries/user'),
          import('@/redux/reducers/paymentsReducer'),
        ]);

      if (credited && store.getState().payment.status === 'pending') {
        store.dispatch(changePaymentStatus('success'));
      }
      void store.dispatch(userApi.endpoints.getMyInfo.initiate());
    })();
  }, 1500);
};

// Express checkout starts once per purchase window the player opened: the
// window's buy view also reappears on its own (status changes, the thank-you
// window, the closing animation) and that must never start another purchase.
let expressIntent = false;

/** Called by the modal system whenever a window opens. */
const noteModalOpened = (renderKind: string): void => {
  if (renderKind.startsWith('buy') && renderKind !== 'buyUsdtModal') {
    expressIntent = true;
  }
};

/** Called by the modal system whenever a window closes. */
const clearExpressIntent = (): void => {
  expressIntent = false;
};

/** True once per opened purchase window. */
const consumeExpressIntent = (): boolean => {
  const intent = expressIntent;
  expressIntent = false;

  return intent;
};

/**
 * Buys the App Store product the server chose for this invoice.
 * onPaid fires as soon as Apple has charged the player — like Telegram's
 * 'paid' — and crediting follows over the websocket, exactly as for Stars.
 */
const purchaseAppleInvoice = async (
  invoice: AppleInvoice,
  onPaid?: () => void,
): Promise<PurchaseResult> => {
  await configure();

  const productType = isSubscription(invoice.productId)
    ? PURCHASE_TYPE.SUBS
    : PURCHASE_TYPE.INAPP;

  // StoreKit returns nothing for products it cannot sell (agreements,
  // metadata, propagation) and the purchase then fails with a vague error
  try {
    const { products } = await NativePurchases.getProducts({
      productIdentifiers: [invoice.productId],
      productType,
    });

    if (!products.length) {
      reportStoreKitProblem(`App Store did not return ${invoice.productId}`);

      return 'failed';
    }
  } catch (error) {
    reportStoreKitProblem(errorMessage(error));

    return 'failed';
  }

  let transaction: Transaction;

  emitCheckout('presenting');

  try {
    transaction = await NativePurchases.purchaseProduct({
      productIdentifier: invoice.productId,
      productType,
      appAccountToken: invoice.appAccountToken,
      autoAcknowledgePurchases: false,
    });
  } catch (error) {
    if (isCancellation(error)) return 'cancelled';
    reportStoreKitProblem(errorMessage(error));

    return 'failed';
  }

  onPaid?.();
  await reconnectSocket();

  let credited = false;

  try {
    // If this fails the transaction stays unfinished and
    // recoverAppleTransactions() delivers it on the next launch.
    credited = await deliver(transaction);
  } catch {
    // see above
  }
  afterPurchaseCredited(credited);

  return 'paid';
};

const IAP_LINK = 'starindustry-iap://purchase';

// started: the server's invoice reached the app; presenting: the App Store
// sheet is about to show; then the result
type CheckoutEvent =
  | 'started'
  | 'presenting'
  | 'paid'
  | 'cancelled'
  | 'failed'
  | 'pending';
const checkoutListeners = new Set<(event: CheckoutEvent) => void>();

/** Progress of the App Store purchase sheet (see TONandStarsBuyButtons). */
const onAppleCheckout = (
  listener: (event: CheckoutEvent) => void,
): (() => void) => {
  checkoutListeners.add(listener);

  return () => {
    checkoutListeners.delete(listener);
  };
};

// How long to wait for the server's invoice before giving up on the hidden
// window (the purchase request itself failed, nothing will open)
const CHECKOUT_START_TIMEOUT_MS = 25000;

/**
 * Express checkout state lives here, not in the purchase window's button:
 * some windows (dice, bingo, scratch) switch to their "in progress" screen
 * the moment the purchase starts, which unmounts the button while the
 * App Store sheet is still up. The window stays hidden until Apple's result.
 */
type ExpressSession = {
  close: () => void;
  started: boolean;
  timer: ReturnType<typeof setTimeout>;
};
let express: ExpressSession | null = null;

const setCheckoutClasses = (hidden: boolean, spinner: boolean): void => {
  const root = document.documentElement;
  root.classList.toggle('apple-checkout', hidden);
  root.classList.toggle('apple-checkout-requesting', spinner);
};

/** Windows that set "in progress" up front must not keep it after a cancel. */
const resetPendingStatus = (): void => {
  void (async (): Promise<void> => {
    const [{ default: store }, { changePaymentStatus }] = await Promise.all([
      import('@/redux/store'),
      import('@/redux/reducers/paymentsReducer'),
    ]);

    if (store.getState().payment.status === 'pending') {
      store.dispatch(changePaymentStatus(undefined));
    }
  })();
};

const endExpressCheckout = (result: CheckoutEvent): void => {
  const session = express;

  if (!session) return;
  express = null;
  clearTimeout(session.timer);
  setCheckoutClasses(false, false);

  if (result === 'paid') return;
  session.close();
  resetPendingStatus();
};

/** Starts an express checkout: the window hides, a spinner shows. */
const beginExpressCheckout = (close: () => void): void => {
  if (express) endExpressCheckout('cancelled');

  express = {
    close,
    started: false,
    timer: setTimeout(() => {
      if (express && !express.started) endExpressCheckout('failed');
    }, CHECKOUT_START_TIMEOUT_MS),
  };
  setCheckoutClasses(true, true);
};

/** The window went away before the App Store sheet: nothing is pending. */
const abandonExpressCheckout = (): void => {
  if (express && !express.started) {
    clearTimeout(express.timer);
    express = null;
    setCheckoutClasses(false, false);
  }
};

const emitCheckout = (event: CheckoutEvent): void => {
  if (express) {
    if (event === 'started') express.started = true;
    else if (event === 'presenting') setCheckoutClasses(true, false);
    else endExpressCheckout(event);
  }
  checkoutListeners.forEach((listener) => listener(event));
};

/**
 * Every purchase flow of the game opens the server's invoice link with
 * Telegram.WebApp.openInvoice(link, callback). In the iOS app the server
 * returns a starindustry-iap:// link instead, and this sends it to StoreKit
 * with the same callback statuses — so none of the ten flows needs changes.
 */
const installAppleInvoiceBridge = (): void => {
  if (!isAppleIapAvailable() || typeof Telegram === 'undefined') return;

  const webApp = Telegram.WebApp;
  const openTelegramInvoice = webApp.openInvoice.bind(webApp);

  type InvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

  webApp.openInvoice = (
    url: string,
    callback?: (status: InvoiceStatus) => void,
  ): void => {
    if (!url.startsWith(IAP_LINK)) {
      return openTelegramInvoice(url, callback);
    }

    const params = new URL(url.replace('starindustry-iap:', 'https:'))
      .searchParams;
    const productId = params.get('productId') ?? '';
    const token = params.get('token') ?? '';
    let reported = false;
    const report = (status: InvoiceStatus): void => {
      if (reported) return;
      reported = true;
      emitCheckout(status);

      // Closing the App Store sheet is an ordinary choice, not an error: the
      // game's flows would show "Something went wrong" for 'cancelled', so
      // they are not told — the express checkout closes the window instead.
      if (status === 'cancelled') return;
      callback?.(status);
    };

    emitCheckout('started');

    void purchaseAppleInvoice(
      { id: token, productId, appAccountToken: token, amount: 0 },
      () => report('paid'),
    ).then(report);
  };
};

/**
 * Launch / foreground: delivers anything paid but not yet credited (crash,
 * lost connection, Ask to Buy approved later, subscription renewals the app
 * sees before the server notification).
 */
const recoverAppleTransactions = async (): Promise<void> => {
  if (!isAppleIapAvailable() || !authToken()) return;

  try {
    await configure();
    const { transactions } = await NativePurchases.getUnfinishedTransactions();

    for (const transaction of transactions) {
      await deliver(transaction).catch(() => false);
    }
  } catch {
    // retried on the next launch
  }
};

let listening = false;

const listenForAppleTransactions = async (): Promise<void> => {
  if (!isAppleIapAvailable() || listening) return;
  listening = true;
  await configure();
  await NativePurchases.addListener('transactionUpdated', (transaction) => {
    if (transaction.needsFinish !== false) {
      void deliver(transaction).catch(() => false);
    }
  });
};

type CatalogDiagnostics = {
  storefront?: string;
  loaded: number;
  sample?: string;
  error?: string;
};

const catalogDiagnostics: CatalogDiagnostics = { loaded: 0 };
let catalog: Promise<Record<string, string>> | null = null;

const allProductIds = (): { inapp: string[]; subs: string[] } => ({
  inapp: [
    ...new Set(
      [...APPLE_PRICE_TIERS, ...Object.values(APPLE_VIP).map((v) => v.price)]
        .map((usd) => packProductId(usd))
        .filter((id): id is string => !!id),
    ),
  ],
  subs: Object.values(APPLE_VIP)
    .map((v) => v.subscription)
    .filter((id): id is string => !!id),
});

/**
 * Every App Store price of the app, fetched once in one StoreKit request
 * (the same way the purchase does: after configure) and shared by all price
 * labels. A failed load is retried on the next request.
 */
const loadCatalog = (): Promise<Record<string, string>> => {
  catalog ??= (async (): Promise<Record<string, string>> => {
    await configure();

    try {
      catalogDiagnostics.storefront = (
        await NativePurchases.getStorefront()
      ).countryCode;
    } catch (error) {
      catalogDiagnostics.storefront = `? (${errorMessage(error)})`;
    }

    const { inapp, subs } = allProductIds();
    const [a, b] = await Promise.all([
      NativePurchases.getProducts({
        productIdentifiers: inapp,
        productType: PURCHASE_TYPE.INAPP,
      }),
      NativePurchases.getProducts({
        productIdentifiers: subs,
        productType: PURCHASE_TYPE.SUBS,
      }),
    ]);
    const products = [...a.products, ...b.products];

    if (!products.length) throw new Error('App Store returned no products');

    catalogDiagnostics.loaded = products.length;
    catalogDiagnostics.sample = `${products[0].identifier.split('.').pop()} = ${products[0].priceString}`;
    catalogDiagnostics.error = undefined;

    return Object.fromEntries(
      products.map((p) => [p.identifier, p.priceString]),
    );
  })().catch((error) => {
    catalogDiagnostics.error = errorMessage(error);
    catalog = null;
    throw error;
  });

  return catalog;
};

/** Localized App Store prices, e.g. "1,99 €", keyed by product id. */
const getApplePrices = async (
  productIds: string[],
): Promise<Record<string, string>> => {
  if (!isAppleIapAvailable() || productIds.length === 0) return {};

  const prices = await loadCatalog();

  return Object.fromEntries(
    productIds.filter((id) => prices[id]).map((id) => [id, prices[id]]),
  );
};

/** Settings → diagnostics line: storefront and price loading state. */
const getAppleCatalogDiagnostics = async (): Promise<CatalogDiagnostics> => {
  await loadCatalog().catch(() => undefined);

  return { ...catalogDiagnostics };
};

/** Apple's own subscription management sheet (cancel, switch plan). */
const manageAppleSubscriptions = (): Promise<void> =>
  NativePurchases.manageSubscriptions();

export {
  abandonExpressCheckout,
  APPLE_VIP,
  appleFallbackPrice,
  type AppleInvoice,
  appleProductFor,
  beginExpressCheckout,
  clearExpressIntent,
  consumeExpressIntent,
  getAppleCatalogDiagnostics,
  getApplePrices,
  installAppleInvoiceBridge,
  isAppleIapAvailable,
  listenForAppleTransactions,
  manageAppleSubscriptions,
  noteModalOpened,
  onAppleCheckout,
  purchaseAppleInvoice,
  type PurchaseResult,
  recoverAppleTransactions,
};
